import {
  Box,
  Primary13Regular,
  RedRed60,
  TextOverflow
} from '@sequencehq/core-components'
import { Fragment } from 'react'

import type { ConditionValueInputComponent } from 'Automations/nodeDefinitions/conditions'
import { NumberInputField } from 'Automations/nodeDefinitions/components'
import { clampNumeric } from './clampNumeric'

export type DurationValue = {
  years: number
  months: number
  days: number
}

const isDurationValue = (value: unknown): value is DurationValue =>
  typeof value === 'object' &&
  value !== null &&
  'years' in value &&
  'months' in value &&
  'days' in value

const readDuration = (value: unknown): DurationValue =>
  isDurationValue(value) ? value : { years: 0, months: 0, days: 0 }

const toComponent = (raw: string): number => {
  const clamped = clampNumeric(raw, { min: 0 })
  return Number.isFinite(clamped) ? Number(clamped) : 0
}

const formatComponent = (n: number): string => (n > 0 ? String(n) : '')

/**
 * Inline editor for durations stored as `{ years, months, days }`. Renders
 * three NumberInputField cells (one per component) as direct grid children;
 * the row's `gridTemplateAreas` must include `years`, `months`, and `days`.
 */
export const DurationValueInput: ConditionValueInputComponent = ({
  'data-testid': dataTestId,
  value,
  onChange,
  validationErrors
}) => {
  const duration = readDuration(value)
  const validationError = validationErrors?.at(0)
  const isInvalid = Boolean(validationError)

  const update = (next: Partial<DurationValue>) =>
    onChange({ value: { ...duration, ...next } })

  return (
    <Fragment>
      <Box gridArea="years" data-testid={appendTestId(dataTestId, 'yearsCell')}>
        <NumberInputField
          data-testid={appendTestId(dataTestId, 'years')}
          value={formatComponent(duration.years)}
          placeholder="0"
          textAlign="right"
          suffix="y"
          onChange={e => update({ years: toComponent(e.target.value) })}
          isInvalid={isInvalid}
          validationErrors={undefined}
        />
      </Box>
      <Box
        gridArea="months"
        data-testid={appendTestId(dataTestId, 'monthsCell')}
      >
        <NumberInputField
          data-testid={appendTestId(dataTestId, 'months')}
          value={formatComponent(duration.months)}
          placeholder="0"
          textAlign="right"
          suffix="m"
          onChange={e => update({ months: toComponent(e.target.value) })}
          isInvalid={isInvalid}
          validationErrors={undefined}
        />
      </Box>
      <Box gridArea="days" data-testid={appendTestId(dataTestId, 'daysCell')}>
        <NumberInputField
          data-testid={appendTestId(dataTestId, 'days')}
          value={formatComponent(duration.days)}
          placeholder="0"
          textAlign="right"
          suffix="d"
          onChange={e => update({ days: toComponent(e.target.value) })}
          isInvalid={isInvalid}
          validationErrors={undefined}
        />
      </Box>
      {validationError && (
        <Box
          data-testid={appendTestId(dataTestId, 'error')}
          gridColumn="years-start / days-end"
          gridRow="2"
          minWidth={0}
          height="16px"
          color={RedRed60}
          {...Primary13Regular}
        >
          <TextOverflow splitPercentage={100}>
            {validationError.message}
          </TextOverflow>
        </Box>
      )}
    </Fragment>
  )
}

const appendTestId = (
  base: string | undefined,
  suffix: string
): string | undefined => (base ? `${base}.${suffix}` : undefined)
